import { useEffect, useRef, useState } from 'react';
import { Play, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { streamPromptTest } from '../../lib/prompts-api';
import { getDefaultSampleQuestion } from '../../config/prompt-test-samples';

interface Props {
  setting: string;
  draftBody: string | null;
}

export function PromptTester({ setting, draftBody }: Props) {
  const [message, setMessage] = useState(getDefaultSampleQuestion(setting));
  const [response, setResponse] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const responseRef = useRef('');

  useEffect(() => {
    setMessage(getDefaultSampleQuestion(setting));
    setResponse('');
    setError(null);
    responseRef.current = '';
  }, [setting]);

  const run = async () => {
    if (running || !message.trim()) return;
    setRunning(true);
    setResponse('');
    setError(null);
    responseRef.current = '';
    try {
      await streamPromptTest(
        setting,
        { message, draftBody: draftBody ?? undefined },
        (chunk) => {
          responseRef.current += chunk;
          setResponse(responseRef.current);
        },
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex flex-col h-full p-3 gap-3 bg-gray-50 border-l border-gray-200">
      <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">▶ Test prompt</div>
      <div className="text-[11px] text-muted-foreground -mt-2">
        {draftBody
          ? 'Testing your unsaved draft.'
          : 'Testing the saved version.'}
      </div>
      <Textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        disabled={running}
        rows={3}
        className="text-xs resize-none"
        placeholder="Sample question…"
      />
      <Button
        size="sm"
        onClick={run}
        disabled={running || !message.trim()}
        className="self-start"
      >
        {running ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}
        {running ? 'Running…' : 'Run sample'}
      </Button>
      {response && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs whitespace-pre-wrap leading-relaxed text-gray-800 overflow-y-auto flex-1 min-h-0">
          {response}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
